// Angular Files
import {
 DEFAULT_CURRENCY_CODE, Inject, Injectable, LOCALE_ID
} from '@angular/core';
import {
 AbstractControl, UntypedFormGroup, NgForm, NgModel
} from '@angular/forms';
import { formatCurrency, getCurrencySymbol } from '@angular/common';

// Teller Online Library Files
import { TellerOnlineSiteMetadataService } from 'libraries/core/src/lib/services/site-metadata.service';
// This service, message.service, and dialog.component form an intentional 3-way cycle
// (dialog.component injects this service; message.service deep-path imports
// dialog.component to dodge a Vite barrel-cycle crash — see message.service's own
// comment). See TellerOnline.Frontend/CLAUDE.md "Vite barrel cycles".
// eslint-disable-next-line import/no-cycle
import { TellerOnlineMessageService } from './message.service';

@Injectable()
export class TellerOnlineValidationService {
    /** Format: a@b.co, allows "+" in the "a" portion */
    public emailRegex: string = '^[a-zA-Z0-9._+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    /**
     * Format: a@b.co, where the "a" portion may use any character RFC 5322 allows unquoted
     * (o'brien@x.com, first&last@x.com). For Item Data email fields, which only Teller reads;
     * emailRegex stays stricter because checkout passes its email on to payment processors.
     * The class characters are escaped so the pattern also compiles under the "v" flag browsers
     * use for the native pattern attribute.
     */
    public formEmailRegex: string = '^[a-zA-Z0-9!#$%&\'*+\\/=?^_`\\{\\|\\}~.\\-]+'
        + '@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    /** Format: A1B2C3 */
    public postalCodeRegex: any = '^[a-zA-Z][0-9][a-zA-Z][0-9][a-zA-Z][0-9]$';

    /** Mask to be used with ngx mask on an input field, to indicate a north american number format */
    public northAmericaPhoneMask: string = '(000) 000-0000';

    /** Mask to be used with ngx mask on an input field, to indicate a NON north american number format */
    public otherPhoneMask: string = '999999999999999';

    public errorTypes: { [key: string]: string } = {
        required: ' is required.',
        minlength: ' must be at least [#] characters long.',
        maxlength: ' cannot exceed [#] characters.',
        min: ' must be at least [#].',
        max: ' must not exceed [#].',
        match: ' does not match.',
        duplicatePrefix: ' must be unique for each connection to this system.',
        disallowedCharacters: ' cannot contain [#].',
        pattern: null
    };

    public fieldErrors: { [key: string]: string } = {
        Email: 'Please enter a real email address.',
        'Confirm Password': 'Password and Confirm Password do not match.',
        'Confirm Routing Number': 'Routing Number and Confirm Routing Number do not match.',
        'Confirm Account Number': 'Account Number and Confirm Account Number do not match.',
        Password: 'Password does not meet requirements.',
        'Zip Code': 'Please enter a valid zip code.',
        'Postal Code': 'Please enter a valid postal code.'
    };

    /** Optional label overrides for form control names. When set, these labels
     *  are used in error messages instead of the control's key name.
     *  e.g. { 'StartDate': 'Date' } would produce "Date is required." instead of "Start Date is required." */
    public fieldLabels: { [key: string]: string } = {};

    public captchaCode: string;

    /** Special events that will be allowed */
    public specialEventList: any = [
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
        'Backspace',
        'Enter',
        'Tab',
        'Delete',
        'Del',
        'Insert',
        'Ins',
        'PageUp',
        'PageDown',
        'PgUp',
        'PgDn'
    ];

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        @Inject(DEFAULT_CURRENCY_CODE) private currencyCode: string,
        private messageService: TellerOnlineMessageService,
        private siteMetadataService: TellerOnlineSiteMetadataService
    ) {
        this.captchaCode = this.siteMetadataService.appConfiguration.captchaClientSideKey ?? this.captchaCode;
    }

    public runValidation(form: NgForm | UntypedFormGroup, captchaValue: boolean = null, quickCheck: boolean = false, additionalErrors: { [key: string]: string } = {}, parentFormSelector: string = ''): boolean {
        let success: boolean = true;
        const captchaMessage: string = 'Please fill out the reCAPTCHA to confirm you are human.';
        const captchaError: boolean = captchaValue != null && !captchaValue;
        const errors: string[] = [];

        if (form) {
            if (form instanceof UntypedFormGroup) {
                const group = form as UntypedFormGroup;
                group.markAllAsTouched();
            }

            this.generatePatternErrors(form, additionalErrors);
            this._retrieveErrors(form.controls, errors, additionalErrors);
        }

        success = errors?.length === 0;
        const additionalErrorCount = additionalErrors ? Object.keys(additionalErrors).length : 0;

        // there's additional errors or the captcha was left blank
        if ((additionalErrors && additionalErrorCount > 0) || (captchaValue != null && !captchaValue)) {
            success = false;
        }

        // Display feedback to the user to do something
        if (!success && !quickCheck) {
            let message = '';
            if (errors?.length < 1 && captchaError) {
                message = captchaMessage;
            } else if (errors?.length < 1 && additionalErrorCount === 1) {
                success = false;
                [message] = Object.values(additionalErrors);
            } else if (
                errors?.length === 1
                && (!additionalErrors || additionalErrorCount === 0)
                && !captchaError
            ) {
                [message] = errors;
            } else {
                const totalErrors = (errors?.length ?? 0)
                    + (captchaError ? 1 : 0) + (additionalErrorCount ?? 0);

                message = `There were ${totalErrors} errors in your submission. Please review the fields for errors and try again.`;
            }
            let notificationDuration = 3000;

            if (message.length > 60) notificationDuration = 6000;
            if (message.length > 120) notificationDuration = 12000;

            this.messageService.notification(message, 'error', notificationDuration);
            this._focusFirstErroredField(parentFormSelector);
        }

        return success;
    }

    public generatePatternErrors(form: NgForm | UntypedFormGroup, additionalErrors: { [key: string]: string } = {}) {
        for (const control in form.controls) {
            if (Object.prototype.hasOwnProperty.call(form.controls, control)) {
                for (const error in form.controls[control].errors) {
                    if (error === 'pattern' || error === 'mask') {
                        additionalErrors[control] = this.fieldErrors[control];
                    }
                }
            }
        }
        return additionalErrors;
    }

    public checkPhoneNumberNorthAmerica(phoneNumber) {
        return phoneNumber.startsWith('1') || phoneNumber.startsWith('+1');
    }

    public getPhoneNumberMask(isNorthAmerica: boolean) {
        return isNorthAmerica ? this.northAmericaPhoneMask : this.otherPhoneMask;
    }

    public getFieldErrorMessage(label, fieldModel: AbstractControl | NgModel, patternError = 'Field must match the provided pattern.') {
        let error = '';
        for (const errorType in fieldModel?.errors) {
            if (Object.prototype.hasOwnProperty.call(fieldModel.errors, errorType)) {
                error = this._mapErrorTypeToMessage(errorType, label, fieldModel, patternError).error;
            }
        }
        return error;
    }

    /** Check whether key event is an approved special event */
    public isSpecialEventCharacter(event: KeyboardEvent): boolean {
        if (this.specialEventList.includes(event.key)
            // Allow: Ctrl+A  1234668
            || (event.key === 'a' && (event.ctrlKey || event.metaKey))
            // Allow: Ctrl+C
            || (event.key === 'c' && (event.ctrlKey || event.metaKey))
            // Allow: Ctrl+V
            || (event.key === 'v' && (event.ctrlKey || event.metaKey))
            // Allow: Ctrl+X
            || (event.key === 'x' && (event.ctrlKey || event.metaKey))
            // Allow: Ctrl+Z
            || (event.key === 'z' && (event.ctrlKey || event.metaKey))
            // Allow: Ctrl+Y
            || (event.key === 'y' && (event.ctrlKey || event.metaKey))) {
            return true;
        }

        return false;
    }

    private _retrieveErrors(controls: { [key: string]: AbstractControl }, errors, additionalErrors: { [key: string]: string } = {}) {
        for (const key in controls) {
            if (Object.prototype.hasOwnProperty.call(controls, key)) {
                const control = controls[key];
                // If this element is a group, and not a control, recurse
                if (control instanceof UntypedFormGroup) {
                    const group = control as UntypedFormGroup;
                    this._retrieveErrors(group.controls, errors, additionalErrors);
                    // if this element is a control, grab it's errors
                } else if (control.invalid && this.fieldErrors[key]) {
                    // If a custom error message is set for this control, use it directly
                    errors.push(this.fieldErrors[key]);

                    // The message covers a pattern or mask error too, which generatePatternErrors
                    // already filed under additionalErrors; drop that copy, or the one field is
                    // counted twice ("There were 2 errors", QA 3.1.23)
                    if (control.errors?.pattern || control.errors?.mask) {
                        delete additionalErrors[key];
                    }
                } else {
                    const label = this.fieldLabels[key] || key;
                    for (const errorType in control.errors) {
                        if (Object.prototype.hasOwnProperty.call(control.errors, errorType)) {
                            const result = this._mapErrorTypeToMessage(
                                errorType, label, control, additionalErrors[key]
                            );
                            errors.push(result.error);
                            if (result.pattern) delete additionalErrors[key];
                        }
                    }
                }
            }
        }
    }

    private _focusFirstErroredField(parentFormSelector: string): void {
        setTimeout(() => {
            const selector = `${parentFormSelector} .mat-form-field-invalid input, ${parentFormSelector} .mat-form-field-invalid mat-select, ${parentFormSelector} .mat-form-field-invalid textarea`;
            const erroredElements = document.querySelectorAll(selector);

            if (erroredElements.length > 0) {
                const firstErroredElement = erroredElements[0] as HTMLElement;
                if (firstErroredElement && typeof firstErroredElement.focus === 'function') {
                    firstErroredElement.focus();
                    // Scroll into view if needed
                    firstErroredElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }, 100);
    }

    private _mapErrorTypeToMessage(errorType: string, label: string, control: AbstractControl | NgModel, patternMessage?: string): { error: string, pattern: boolean } {
        // Capitalize first letter and add spaces before capital letters (e.g., 'firstName' -> 'First Name')
        const formattedLabel = this._formatFieldLabel(label);
        let errorMessage = formattedLabel + this.errorTypes[errorType];
        let pattern = false;

        switch (errorType) {
            case 'minlength':
            case 'maxlength':
                errorMessage = errorMessage.replace('[#]', control.errors[errorType].requiredLength);
                break;
            case 'disallowedCharacters':
                errorMessage = errorMessage
                    .replace('[#]', control.errors[errorType].characters.join(', '));
                break;
            case 'max':
            case 'min': {
                // assume all min/max validation is currency, will need an update if otherwise
                let limit: number;
                if (errorType === 'max') limit = control.errors[errorType].max;
                if (errorType === 'min') limit = control.errors[errorType].min;

                errorMessage = errorMessage.replace('[#]', formatCurrency(limit, this.locale, getCurrencySymbol(this.currencyCode, 'narrow')));
                break;
            }
            case 'pattern':
            case 'mask':
                // A field with no custom message (the billing phone's mask) has none to pass,
                // and an undefined message breaks the popup
                errorMessage = patternMessage
                    ?? `${formattedLabel} must match the provided format.`;
                pattern = true;
                break;
            default:
                break;
        }
        return { error: errorMessage, pattern };
    }

    /**
     * Formats a form control name into a user-friendly label.
     * - Capitalizes the first letter
     * - Adds spaces before capital letters (for camelCase names like 'firstName' -> 'First Name')
     */
    private _formatFieldLabel(label: string): string {
        if (!label) return label;

        // Add space before capital letters (for camelCase), then capitalize first letter
        const withSpaces = label.replace(/([A-Z])/g, ' $1').trim();
        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    }
}
